// Round 22 (R-02) — Mailing Address: US / APO-FPO-DPO / Foreign.
//
// THE single source of truth for how a mailing address is typed, validated and
// rendered. Five separate places read these fields back out (Step 6 review,
// sponsor copy, application PDF, admin CSV, tracker Roster); before this module
// each of them formatted a US-style "street, city, state zip" string inline, so
// adding two more address shapes to five hand-rolled formatters would have
// guaranteed drift. Every caller now goes through mailingAddressLines() /
// trackerCity() / trackerCounty() instead.
//
// STORAGE NOTE (corrects an earlier assumption in the project KB): these fields
// are NOT SharePoint list columns. The whole ApplicationData object is stored as
// a JSON string in the Drafts list's ApplicationData column, so adding address
// fields needs no SharePoint provisioning at all. The only address values that
// reach real columns are the tracker Roster's "Home City" / "Home County" (an
// Excel sheet) plus the Round 22 "Address Type" trailing column.

export const ADDRESS_TYPES = ["US", "APO_FPO_DPO", "Foreign"] as const;
export type AddressType = (typeof ADDRESS_TYPES)[number];

export const ADDRESS_TYPE_LABELS: Record<AddressType, string> = {
  US: "US Mailing Address",
  APO_FPO_DPO: "APO / FPO / DPO (military or diplomatic mail)",
  Foreign: "Foreign Address",
};

export const APO_OFFICES = ["APO", "FPO", "DPO"] as const;
export type ApoOffice = (typeof APO_OFFICES)[number];

export const APO_OFFICE_LABELS: Record<ApoOffice, string> = {
  APO: "APO — Army/Air Post Office",
  FPO: "FPO — Fleet Post Office",
  DPO: "DPO — Diplomatic Post Office",
};

/**
 * The 50 US states plus the District of Columbia — the options on the US
 * mailing-address path, stored as the 2-letter code.
 *
 * DC is a first-class entry, not an afterthought: applicants from the District
 * are a near-annual occurrence for both programs.
 *
 * US territories (PR, GU, VI, AS, MP) are deliberately absent — none has ever
 * applied. Adding one later is a one-line change here; the select and the
 * validator both read this list.
 */
export const US_STATES: ReadonlyArray<{ code: string; name: string }> = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "DC", name: "District of Columbia" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
];

export const US_STATE_CODES: readonly string[] = US_STATES.map((s) => s.code);

/** True when the value is one of the 51 selectable state codes. */
export function isUsStateCode(value: string): boolean {
  return US_STATE_CODES.includes(value);
}

export const APO_STATE_CODES = ["AA", "AE", "AP"] as const;
export type ApoStateCode = (typeof APO_STATE_CODES)[number];

export const APO_STATE_CODE_LABELS: Record<ApoStateCode, string> = {
  AA: "AA — Armed Forces Americas",
  AE: "AE — Armed Forces Europe, Middle East, Africa, Canada",
  AP: "AP — Armed Forces Pacific",
};

/** Reference examples rendered in the APO/FPO/DPO helper text (from USPS). */
export const APO_EXAMPLES: ReadonlyArray<{ label: string; lines: readonly string[] }> = [
  { label: "Army/Air Post Office", lines: ["PFC JOHN DOE", "PSC 3 BOX 4120", "APO AE 09021"] },
  { label: "Fleet Post Office", lines: ["SEAMAN JOSEPH SMITH", "UNIT 100100 BOX 4120", "FPO AP 96691"] },
  { label: "Diplomatic Post Office", lines: ["JOHN ADAMS", "UNIT 8400 BOX 0000", "DPO AE 09498-0048"] },
];

export const USPS_INTERNATIONAL_URL =
  "https://faq.usps.com/s/article/International-addressing-requirements";

/**
 * Outside the Continental United States. Written into the County field for both
 * non-US address types so the roster has one standard value instead of families
 * free-texting "Overseas" (the inconsistency staff testing flagged).
 */
export const OCONUS = "OCONUS";

/**
 * 5-digit or ZIP+4. Used for BOTH the US ZIP and the APO/FPO/DPO ZIP: USPS calls
 * the military ZIP a "9-digit ZIP", but two of the three official reference
 * examples above are plain 5-digit, and families often only know the short form.
 * Requiring ZIP+4 would reject the very examples shown in the helper text.
 */
export const ZIP_RE = /^\d{5}(-\d{4})?$/;

/** The five free-text Foreign lines, in render/storage order. */
export const FOREIGN_LINE_KEYS = [
  "foreignAddressLine1",
  "foreignAddressLine2",
  "foreignAddressLine3",
  "foreignAddressLine4",
  "foreignAddressLine5",
] as const;

export type ForeignLineKey = (typeof FOREIGN_LINE_KEYS)[number];

/**
 * The address slice of Step2BasicInfo. Deliberately all-optional: server-side
 * readers (tracker mapper, admin CSV, PDF) map stored drafts that may predate
 * this round, exactly as the Round 11/18/20 back-fills had to.
 */
export interface MailingAddressFields {
  addressType?: AddressType | string | null;
  homeStreet?: string;
  homeCity?: string;
  homeCounty?: string;
  homeState?: string;
  homeZip?: string;
  apoRecipientName?: string;
  apoUnitBox?: string;
  apoOffice?: string;
  apoStateCode?: string;
  apoZip?: string;
  foreignAddressLine1?: string;
  foreignAddressLine2?: string;
  foreignAddressLine3?: string;
  foreignAddressLine4?: string;
  foreignAddressLine5?: string;
}

/**
 * The stored shape of the address slice of Step2BasicInfo — every field
 * present and typed, no optionals. Step2BasicInfo declares these fields inline
 * (with their per-path commentary); this interface exists so the empty-value
 * factory below can be typed without types/index.ts and mailingAddress.ts
 * importing each other in a circle.
 */
export interface StoredMailingAddress {
  addressType: AddressType;
  homeStreet: string;
  homeCity: string;
  homeCounty: string;
  homeState: string;
  homeZip: string;
  apoRecipientName: string;
  apoUnitBox: string;
  apoOffice: ApoOffice | "";
  apoStateCode: ApoStateCode | "";
  apoZip: string;
  foreignAddressLine1: string;
  foreignAddressLine2: string;
  foreignAddressLine3: string;
  foreignAddressLine4: string;
  foreignAddressLine5: string;
}

/**
 * Empty values for every mailing-address field. Used for new-draft defaults and
 * as the base of test fixtures, so adding a future address field does not mean
 * hand-editing every literal that builds a Step2BasicInfo.
 *
 * `homeState` starts EMPTY. It used to default to "MD", which meant an
 * applicant who never touched the field still had "MD" silently recorded as
 * though they had chosen it — indistinguishable in the data from a deliberate
 * Maryland selection. The state is now an explicit choice from US_STATES.
 */
export function emptyMailingAddress(homeState = ""): StoredMailingAddress {
  return {
    addressType: "US",
    homeStreet: "",
    homeCity: "",
    homeCounty: "",
    homeState,
    homeZip: "",
    apoRecipientName: "",
    apoUnitBox: "",
    apoOffice: "",
    apoStateCode: "",
    apoZip: "",
    foreignAddressLine1: "",
    foreignAddressLine2: "",
    foreignAddressLine3: "",
    foreignAddressLine4: "",
    foreignAddressLine5: "",
  };
}

/**
 * Any unrecognized or missing value reads as "US" — the behavior every draft
 * saved before Round 22 must keep. Do not tighten this to throw: a draft with no
 * addressType key is the normal case for existing applicants, not an error.
 */
export function normalizeAddressType(value: unknown): AddressType {
  return (ADDRESS_TYPES as readonly string[]).includes(value as string)
    ? (value as AddressType)
    : "US";
}

const clean = (v: string | undefined | null): string => (typeof v === "string" ? v.trim() : "");

/** The APO/FPO/DPO last line, e.g. "APO AE 09021". */
function apoLastLine(a: MailingAddressFields): string {
  return [clean(a.apoOffice), clean(a.apoStateCode), clean(a.apoZip)].filter(Boolean).join(" ");
}

/**
 * The US "City, ST 21204" line.
 *
 * Returns "" when neither a city nor a ZIP is present, so a partly-filled
 * address cannot render as a bare, meaningless state line in the review grid
 * or PDF. (This guard predates dropping the "MD" default, and still matters:
 * an applicant can pick a state before typing a city or ZIP.)
 */
function usLocalityLine(a: MailingAddressFields): string {
  const city = clean(a.homeCity);
  const zip = clean(a.homeZip);
  if (!city && !zip) return "";
  const stateZip = [clean(a.homeState), zip].filter(Boolean).join(" ");
  return [city, stateZip].filter(Boolean).join(", ");
}

/**
 * The address as display lines, already correct for its type. Empty entries are
 * dropped, so a partly-filled draft renders what exists instead of stray commas.
 *
 * An APO/FPO/DPO block deliberately carries NO city or country name — including
 * one routes the mail into the destination country's postal network instead of
 * the military one. A Foreign block is printed verbatim, exactly as the
 * applicant entered it for their country's format.
 */
export function mailingAddressLines(a: MailingAddressFields): string[] {
  switch (normalizeAddressType(a.addressType)) {
    case "APO_FPO_DPO":
      return [clean(a.apoRecipientName), clean(a.apoUnitBox), apoLastLine(a)].filter(Boolean);
    case "Foreign":
      return FOREIGN_LINE_KEYS.map((k) => clean(a[k])).filter(Boolean);
    case "US":
    default:
      return [clean(a.homeStreet), usLocalityLine(a)].filter(Boolean);
  }
}

/** Single-line form for the review grid / PDF row. "—" when nothing is entered. */
export function mailingAddressOneLine(a: MailingAddressFields): string {
  const lines = mailingAddressLines(a);
  return lines.length > 0 ? lines.join(", ") : "—";
}

/**
 * County for display/export. Both non-US types are always OCONUS — it is a
 * derived value, never user-entered on those paths.
 */
export function mailingAddressCounty(a: MailingAddressFields): string {
  return normalizeAddressType(a.addressType) === "US" ? clean(a.homeCounty) : OCONUS;
}

/**
 * Value for the tracker Roster's existing "Home City" column. Keeps that column
 * meaningful for military/overseas families instead of leaving it blank:
 * "APO AE" for military mail, the literal "Foreign" for a foreign address.
 */
export function trackerCity(a: MailingAddressFields): string {
  switch (normalizeAddressType(a.addressType)) {
    case "APO_FPO_DPO":
      return [clean(a.apoOffice), clean(a.apoStateCode)].filter(Boolean).join(" ");
    case "Foreign":
      return "Foreign";
    case "US":
    default:
      return clean(a.homeCity);
  }
}

/** Value for the tracker Roster's existing "Home County" column. */
export function trackerCounty(a: MailingAddressFields): string {
  return mailingAddressCounty(a);
}

export interface MailingAddressIssue {
  /** Field key, matching the `errors` record the step components read. */
  path: string;
  message: string;
}

/**
 * Every per-type required-field rule for the mailing address, in one place.
 *
 * This is called from BOTH the step1BasicInfoSchema `.superRefine` (the
 * submit/server backstop) AND directly from the live Step 1 error builder in
 * App.tsx. The standalone call is required, not redundant: Zod skips an
 * object-level `.superRefine` once the object parse aborts, which happens
 * whenever any other required Step 1 field is still blank — so on a partly
 * filled step the schema rule never runs. Same Round 14 gotcha that forced the
 * standalone studentParentEmailsMatch / otcAcknowledgementComplete checks.
 *
 * Because these rules are conditional, the address fields can no longer be
 * object-level `requiredString()` in the schema (that would fire US rules
 * against an APO entry). They are plain `z.string()` there and required here.
 */
export function mailingAddressIssues(a: MailingAddressFields): MailingAddressIssue[] {
  const issues: MailingAddressIssue[] = [];
  const require = (path: string, value: string, label: string) => {
    if (!value) issues.push({ path, message: `${label} is required` });
  };

  switch (normalizeAddressType(a.addressType)) {
    case "APO_FPO_DPO": {
      require("apoRecipientName", clean(a.apoRecipientName), "Recipient's full name");
      require("apoUnitBox", clean(a.apoUnitBox), "PSC/UNIT and Box number");
      if (!(APO_OFFICES as readonly string[]).includes(clean(a.apoOffice))) {
        issues.push({ path: "apoOffice", message: "Please choose APO, FPO or DPO" });
      }
      if (!(APO_STATE_CODES as readonly string[]).includes(clean(a.apoStateCode))) {
        issues.push({ path: "apoStateCode", message: "Please choose AA, AE or AP" });
      }
      const apoZip = clean(a.apoZip);
      if (!apoZip) {
        issues.push({ path: "apoZip", message: "ZIP code is required" });
      } else if (!ZIP_RE.test(apoZip)) {
        issues.push({ path: "apoZip", message: "Enter a ZIP as 09021 or 09498-0048" });
      }
      break;
    }
    case "Foreign": {
      // Lines 3-5 stay optional: address formats vary by country and many need
      // fewer than five lines. Requiring the first two keeps a single stray line
      // from being submitted as a whole mailing address.
      require("foreignAddressLine1", clean(a.foreignAddressLine1), "Address line 1");
      require("foreignAddressLine2", clean(a.foreignAddressLine2), "Address line 2");
      break;
    }
    case "US":
    default: {
      require("homeStreet", clean(a.homeStreet), "Street address");
      require("homeCity", clean(a.homeCity), "City");
      require("homeCounty", clean(a.homeCounty), "County");
      // Still required, exactly as before — but no longer satisfiable by a
      // default nobody chose. A draft saved while the field was a free-text box
      // may hold something outside the list (e.g. "Maryland"); that value is
      // left in the data untouched and simply prompts a selection on next edit.
      const state = clean(a.homeState);
      if (!state) {
        issues.push({ path: "homeState", message: "Please select a state" });
      } else if (!isUsStateCode(state)) {
        issues.push({ path: "homeState", message: "Please select a state from the list" });
      }
      const zip = clean(a.homeZip);
      if (!zip) {
        issues.push({ path: "homeZip", message: "ZIP is required" });
      } else if (!ZIP_RE.test(zip)) {
        issues.push({ path: "homeZip", message: "Please enter a valid ZIP code" });
      }
      break;
    }
  }
  return issues;
}

/** True when the selected address type has no outstanding required-field issues. */
export function mailingAddressComplete(a: MailingAddressFields): boolean {
  return mailingAddressIssues(a).length === 0;
}
